import { Scene, TransformNode } from "babylonjs";
import AppEvents from "@core/events/events";
import { AppMode } from "@core/context/types";

/**
 * Manages anchors for different modes (ground, image, VPS)
 */
export class AnchorManager {
  private scene: Scene;
  private imageAnchor?: TransformNode;
  private groundAnchor?: TransformNode;
  private activeAnchor?: TransformNode;

  constructor(scene: Scene) {
    this.scene = scene;
    this.initModeListener();
    this.initAnchorEventListeners();
  }

  private initModeListener(): void {
    AppEvents.on('app-mode-changed', this.onModeChanged.bind(this));
  }

  private initAnchorEventListeners(): void {
    AppEvents.on('anchor-created', (anchor: TransformNode) => {
      this.activeAnchor = anchor;
    });

    AppEvents.on('anchor-removed', (anchor: TransformNode) => {
      if (anchor === this.activeAnchor) {
        this.activeAnchor = undefined;
      }
    });
  }

  private onModeChanged(mode: AppMode): void {
    if (mode !== AppMode.IMAGE) {
      this.cleanupImageAnchor();
    }
    if (mode !== AppMode.GROUND) {
      this.cleanupGroundAnchor();
    }
  }

  /**
   * Create an image anchor for image tracking mode
   */
  public createImageAnchor(): TransformNode {
    if (this.imageAnchor) {
      return this.imageAnchor;
    }

    const anchor = new TransformNode("imageAnchor", this.scene);
    this.imageAnchor = anchor;

    // Create a visual debug cube
    //const cube = MeshBuilder.CreateBox("cube", { size: 0.2 }, this.scene);
    //cube.parent = anchor;

    AppEvents.emit('anchor-created', anchor);
    return anchor;
  }

  /**
   * Clean up the image anchor
   */
  public cleanupImageAnchor(): void {
    if (this.imageAnchor) {
      AppEvents.emit('anchor-removed', this.imageAnchor);
      this.imageAnchor.dispose();
      this.imageAnchor = undefined;
    }
  }

  /**
   * Create a ground anchor for ground placement mode
   */
  public createGroundAnchor(): TransformNode {
    if (this.groundAnchor) {
      return this.groundAnchor;
    }

    const anchor = new TransformNode("groundAnchor", this.scene);
    this.groundAnchor = anchor;

    // Create a visual debug cube
    //const cube = MeshBuilder.CreateBox("cube", { size: 0.2 }, this.scene);
    //cube.parent = anchor;

    AppEvents.emit('anchor-created', anchor);
    return anchor;
  }

  /**
   * Clean up the ground anchor
   */
  public cleanupGroundAnchor(): void {
    if (this.groundAnchor) {
      AppEvents.emit('anchor-removed', this.groundAnchor);
      this.groundAnchor.dispose();
      this.groundAnchor = undefined;
    }
  }

  /**
   * Get the current active anchor
   */
  public getActiveAnchor(): TransformNode | undefined {
    return this.activeAnchor;
  }

  /**
   * Get the image anchor (may be undefined)
   */
  public getImageAnchor(): TransformNode | undefined {
    return this.imageAnchor;
  }

  /**
   * Get the ground anchor (may be undefined)
   */
  public getGroundAnchor(): TransformNode | undefined {
    return this.groundAnchor;
  }

  /**
   * Clean up all anchors
   */
  public dispose(): void {
    this.cleanupImageAnchor();
    this.cleanupGroundAnchor();
    this.activeAnchor = undefined;
  }
}

